import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import * as Sentry from "@sentry/browser";
import { colors, margins } from "style";
import {
  INTERVIEW_QUESTION_PHASE,
  MIN_RECORDING_SIZE_BYTES,
  QUESTION_COUNTDOWN_SECONDS,
  QUESTION_PRE_TIMER_SECONDS,
  RECORDING_TIME_SECONDS,
} from "constants/index";
import Card from "components/Card";
import { PrimaryButton } from "components/Buttons";
import { InterviewSubtitle, InterviewTitle } from "components/VideoPresentation";
import { UploadProgress } from "components/Form";
import { useAudioDetection, useModalConfirmation } from "hooks";
import { MODAL_SIZE } from "components/Modal";
import { formatCountDown } from "utils";

/**
 * QuestionStep Component
 *
 * @param {object}    stream
 * @param {object}    question
 * @param {number}    questionNumber
 * @param {number}    totalQuestions
 * @param {function}  onAnswerRecorded
 * @param {boolean}   uploading
 * @param {object}    uploadError
 * @param {number}    uploadProgress
 * @param {object}    retryInfo
 */
const QuestionStep = ({
  stream,
  question,
  questionNumber,
  totalQuestions,
  onAnswerRecorded,
  uploading,
  uploadError,
  uploadProgress,
  retryInfo,
}) => {
  const { startAudioDetection, stopAudioDetection, audioDetectedRef } = useAudioDetection(stream);
  const { show } = useModalConfirmation();

  const cameraRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const questionVideoRef = useRef(null);

  const [phase, setPhase] = useState(INTERVIEW_QUESTION_PHASE.intro);
  const [preTimer, setPreTimer] = useState(15);
  const [countdown, setCountdown] = useState(3);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(120);

  const hasQuestionVideo = Boolean(question.video);

  /* Attach stream to camera */
  useEffect(() => {
    if (cameraRef.current && stream) {
      cameraRef.current.srcObject = stream;
    }
  }, [stream, phase]);

  /* Reset when question changes */
  useEffect(() => {
    setPhase(hasQuestionVideo ? INTERVIEW_QUESTION_PHASE.intro : INTERVIEW_QUESTION_PHASE.waiting);
    setPreTimer(QUESTION_PRE_TIMER_SECONDS);
    setCountdown(QUESTION_COUNTDOWN_SECONDS);
    setRecordingTimeLeft(question?.timeLimitSeconds || RECORDING_TIME_SECONDS);
    chunksRef.current = [];
  }, [questionNumber, question]);

  /* Question video ended */
  const handleVideoEnded = () => setPhase(INTERVIEW_QUESTION_PHASE.waiting);

  /* Waiting countdown */
  useEffect(() => {
    if (phase !== INTERVIEW_QUESTION_PHASE.waiting) return;
    if (preTimer <= 0) {
      setPhase(INTERVIEW_QUESTION_PHASE.countdown);
      return;
    }
    const id = setTimeout(() => setPreTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, preTimer]);

  /* Pre-record countdown */
  useEffect(() => {
    if (phase !== INTERVIEW_QUESTION_PHASE.countdown) return;
    if (countdown <= 0) {
      startRecording();
      return;
    }
    const id = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, countdown]);

  /* Recording timer */
  useEffect(() => {
    if (phase !== INTERVIEW_QUESTION_PHASE.recording) return;
    if (recordingTimeLeft <= 0) {
      stopRecording();
      return;
    }
    const id = setTimeout(() => setRecordingTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, recordingTimeLeft]);

  const startRecording = () => {
    chunksRef.current = [];

    const videoTrack = stream?.getVideoTracks()?.[0];

    if (!videoTrack || videoTrack.readyState !== "live") {
      // There's no video track, ask user to fix camera first.
      alert("Camera is unavailable. Please check your camera and try again.");
      setPhase(INTERVIEW_QUESTION_PHASE.waiting);
      return;
    }

    let recorder;

    try {
      recorder = new MediaRecorder(stream, {
        mimeType: "video/webm; codecs=vp8,opus",
        videoBitsPerSecond: 700000,
        audioBitsPerSecond: 64000,
      });
    } catch (e) {
      // Try to initialize recorder with default config if a browser (sometimes old mobile safari) doesn't support video/webm.
      if (e.name === "NotSupportedError") {
        recorder = new MediaRecorder(stream);
      } else {
        Sentry.captureException(e);
        alert("Unable to start recording. Please make sure you are using a compatible browser.");
        return;
      }
    }

    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const file = new File(chunksRef.current, `answer-${question.id}-${Date.now()}.webm`, { type: "video/webm" });

      // Prevent empty/corrupt MediaRecorder output from being submitted.
      if (file.size < MIN_RECORDING_SIZE_BYTES) {
        Sentry.captureMessage("Invalid interview recording generated.", {
          level: "error",
          extra: {
            questionId: question.id,
            fileSize: file.size,
            chunksCount: chunksRef.current.length,
            chunkSizes: chunksRef.current.map((chunk) => chunk.size),
            streamActive: stream?.active,
            videoTrackState: stream?.getVideoTracks()?.[0]?.readyState,
            videoTrackMuted: stream?.getVideoTracks()?.[0]?.muted,
          },
        });

        alert("Your video was not recorded correctly. Please check your camera and try recording your answer again.");
        handleRerecord();
        return;
      }

      if (audioDetectedRef.current) return onAnswerRecorded(file);

      const response = await show(
        <FormattedMessage id="VideoPresentation.QuestionStep.NoAudioDetected" />,
        <>
          <FormattedMessage id="VideoPresentation.QuestionStep.NoAudioDetectedDescription" />
        </>,
        {
          primaryButtonMessageId: "VideoPresentation.QuestionStep.Retry",
          secondaryButtonMessageId: "VideoPresentation.QuestionStep.SubmitAnyway",
          size: MODAL_SIZE.medium,
        }
      );

      if (response) {
        handleRerecord();
      } else {
        onAnswerRecorded(file);
      }
    };

    startAudioDetection();
    recorder.start();
    setPhase(INTERVIEW_QUESTION_PHASE.recording);
  };

  const stopRecording = async () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      await stopAudioDetection();
      recorderRef.current.stop();
    }
  };

  const handleRerecord = () => {
    chunksRef.current = [];

    setCountdown(QUESTION_COUNTDOWN_SECONDS);
    setRecordingTimeLeft(RECORDING_TIME_SECONDS);

    setPhase(INTERVIEW_QUESTION_PHASE.countdown);
  };

  const handleAnswerNow = () => setPhase(INTERVIEW_QUESTION_PHASE.countdown);
  const handleSubmitClick = () => phase === INTERVIEW_QUESTION_PHASE.recording && stopRecording();

  return (
    <div css={styles.container}>
      <InterviewTitle
        titleId="VideoPresentation.QuestionStep.Title"
        values={{ current: questionNumber, total: totalQuestions, title: question.title }}
        label={
          <FormattedMessage
            id="VideoPresentation.QuestionStep.Label"
            values={{ current: questionNumber, total: totalQuestions }}
          />
        }
      />
      {(question.description || question.descriptionJson) && (
        <InterviewSubtitle description={question.description} descriptionJson={question.descriptionJson} />
      )}

      <Card css={styles.card}>
        <div css={styles.background_video_wrapper(phase)}>
          {hasQuestionVideo ? (
            <video
              ref={questionVideoRef}
              src={question.video}
              autoPlay
              onEnded={handleVideoEnded}
              css={styles.background_video}
            />
          ) : (
            <div css={styles.no_video_background} />
          )}
        </div>

        {phase !== INTERVIEW_QUESTION_PHASE.intro && (
          <>
            <div css={styles.camera_window}>
              <video ref={cameraRef} autoPlay muted playsInline css={styles.camera_video(uploading)} />

              <div css={styles.overlay_center}>
                {phase === INTERVIEW_QUESTION_PHASE.waiting && (
                  <div css={styles.pre_start_text}>
                    <FormattedMessage
                      id="VideoPresentation.QuestionStep.RecordingStartsIn"
                      values={{ time: <strong>{preTimer}</strong> }}
                    />
                  </div>
                )}

                {phase === INTERVIEW_QUESTION_PHASE.countdown && <div css={styles.countdown_number}>{countdown}</div>}
              </div>

              {phase === INTERVIEW_QUESTION_PHASE.recording && (
                <>
                  <div css={styles.rec_indicator}>
                    <span css={styles.rec_dot} /> REC
                  </div>
                  <div css={styles.timer_badge}>{formatCountDown(recordingTimeLeft)}</div>
                </>
              )}
            </div>
            {uploadProgress > 0 && (
              <UploadProgress
                progress={uploadProgress}
                textId={`Form.UploadProgress.${retryInfo ? "Retrying" : "SubmittingAnswer"}`}
                textIdValues={{
                  progress: Math.round(uploadProgress),
                  attempt: retryInfo ? retryInfo.attempt + 1 : 1,
                  maxRetries: retryInfo ? retryInfo.maxRetries : 1,
                }}
                overlay
              />
            )}
          </>
        )}
        <div css={styles.actions}>
          {phase !== INTERVIEW_QUESTION_PHASE.recording && (
            <PrimaryButton onClick={handleAnswerNow} disabled={phase !== INTERVIEW_QUESTION_PHASE.waiting}>
              <FormattedMessage id="VideoPresentation.QuestionStep.AnswerNow" />
            </PrimaryButton>
          )}

          {phase === INTERVIEW_QUESTION_PHASE.recording && (
            <PrimaryButton
              onClick={handleSubmitClick}
              disabled={uploading || recordingTimeLeft > RECORDING_TIME_SECONDS - 5}
            >
              <FormattedMessage id={`VideoPresentation.QuestionStep.${uploading ? "Submitting" : "SubmitAnswer"}`} />
            </PrimaryButton>
          )}

          {uploadError && <p css={styles.error_text}>{uploadError.message}</p>}
        </div>
      </Card>
    </div>
  );
};

const styles = {
  container: css`
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  card: css`
    padding: 24px;
    padding-bottom: 32px;
    border-radius: 20px;
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.06);
    display: flex;
    flex-direction: column;
    gap: 18px;
    position: relative;
    overflow: visible;
  `,
  badge: css`
    padding: 4px 12px;
    align-self: flex-start;
    border-radius: 999px;
    background: ${colors.purpleRainLight3};
    border: 1px solid ${colors.purpleRainDark2};
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${colors.purpleRainBase};
    text-transform: uppercase;
  `,
  background_video_wrapper: (phase) => css`
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    overflow: hidden;
    opacity: ${phase === INTERVIEW_QUESTION_PHASE.intro ? 1 : 0.3};
    filter: ${phase === INTERVIEW_QUESTION_PHASE.intro ? "none" : "blur(3px)"};
    transition: opacity 0.3s ease, filter 0.3s ease;
  `,
  no_video_background: css`
    width: 100%;
    height: 100%;
    background: ${colors.grayAnatomyLight1};
  `,
  background_video: css`
    width: 100%;
    height: 100%;
    background: ${colors.black};
  `,
  camera_window: css`
    position: absolute;
    top: 70px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    aspect-ratio: 4 / 3;
    border-radius: 18px;
    overflow: hidden;
    background: black;
    z-index: 10;
    box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  `,
  camera_video: (uploading) => css`
    width: 100%;
    height: 100%;
    transform: scaleX(-1);
    opacity: ${uploading ? 0.35 : 1};
    filter: ${uploading ? "grayscale(0.6)" : "none"};
  `,
  overlay_center: css`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  pre_start_text: css`
    font-size: 28px;
    padding: 12px 24px;
    border-radius: 12px;
    color: ${colors.white};
    background: rgba(0, 0, 0, 0.55);
  `,
  countdown_number: css`
    font-size: 82px;
    color: ${colors.white};
    font-weight: 700;
  `,
  rec_indicator: css`
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.65);
    padding: 6px 12px;
    border-radius: 999px;
    color: ${colors.white};
    display: flex;
    align-items: center;
    gap: 6px;
  `,
  rec_dot: css`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${colors.red};
    box-shadow: 0 0 6px ${colors.red};
  `,
  timer_badge: css`
    position: absolute;
    bottom: 12px;
    left: 12px;
    background: rgba(0, 0, 0, 0.65);
    padding: 6px 12px;
    border-radius: 999px;
    color: ${colors.white};
    font-size: 20px;
    font-weight: 600;
  `,
  actions: css`
    display: flex;
    justify-content: center;
    margin-top: ${margins.xxsmall};
  `,
  error_text: css`
    color: ${colors.red};
    text-align: center;
    margin-top: 8px;
  `,
  inline_camera_wrapper: css`
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 16px;
    overflow: hidden;
    background: ${colors.black};
    position: relative;
  `,
  inline_camera_video: css`
    width: 100%;
    height: 100%;
    transform: scaleX(-1);
  `,
};

QuestionStep.propTypes = {
  stream: PropTypes.object.isRequired,
  question: PropTypes.object.isRequired,
  questionNumber: PropTypes.number.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  onAnswerRecorded: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  uploadError: PropTypes.object,
  uploadProgress: PropTypes.number,
  retryInfo: PropTypes.object,
};

export default QuestionStep;
